import React from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTour } from '../../contexts/TourContext';

interface TourTooltipProps {
  step: number;
  totalSteps: number;
  title: string;
  content: string;
  position: { top: number; left: number };
  placement: 'top' | 'bottom' | 'left' | 'right';
}

export default function TourTooltip({ 
  step, 
  totalSteps, 
  title, 
  content, 
  position, 
  placement 
}: TourTooltipProps) {
  const { nextStep, prevStep, endTour } = useTour();

  // Calculate tooltip position and arrow placement
  const getTooltipStyles = () => {
    const styles: React.CSSProperties = {
      position: 'absolute',
      zIndex: 1000,
    };

    const offset = 15; // Distance from target element

    switch (placement) {
      case 'top':
        styles.top = position.top - offset;
        styles.left = position.left;
        styles.transform = 'translate(-50%, -100%)';
        break;
      case 'bottom':
        styles.top = position.top + offset;
        styles.left = position.left;
        styles.transform = 'translateX(-50%)';
        break;
      case 'left':
        styles.top = position.top;
        styles.left = position.left - offset;
        styles.transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        styles.top = position.top;
        styles.left = position.left + offset;
        styles.transform = 'translateY(-50%)';
        break;
    }

    return styles;
  };

  // Get arrow class based on placement
  const getArrowClass = () => {
    switch (placement) {
      case 'top': return 'bottom-[-8px] left-1/2 transform -translate-x-1/2 border-t-primary-500 border-l-transparent border-r-transparent border-b-transparent';
      case 'bottom': return 'top-[-8px] left-1/2 transform -translate-x-1/2 border-b-primary-500 border-l-transparent border-r-transparent border-t-transparent';
      case 'left': return 'right-[-8px] top-1/2 transform -translate-y-1/2 border-l-primary-500 border-t-transparent border-b-transparent border-r-transparent';
      case 'right': return 'left-[-8px] top-1/2 transform -translate-y-1/2 border-r-primary-500 border-t-transparent border-b-transparent border-l-transparent';
    }
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-lg p-4 w-72 border border-primary-100"
      style={getTooltipStyles()}
    >
      {/* Arrow */}
      <div className={`absolute w-0 h-0 border-8 ${getArrowClass()}`} />

      {/* Close button */}
      <button 
        onClick={endTour}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        aria-label="Close tour"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Content */}
      <div className="mb-4 pt-2">
        <h3 className="font-bold text-lg text-primary-700 mb-2">{title}</h3>
        <p className="text-gray-600">{content}</p>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Step {step + 1} of {totalSteps}
        </div>
        <div className="flex gap-2">
          {step > 0 && (
            <button
              onClick={prevStep}
              className="p-1 rounded-full hover:bg-gray-100"
              aria-label="Previous step"
            >
              <ChevronLeft className="h-5 w-5 text-primary-600" />
            </button>
          )}
          <button
            onClick={nextStep}
            className="bg-primary-500 text-white px-3 py-1 rounded-md hover:bg-primary-600 transition-colors flex items-center gap-1"
          >
            {step === totalSteps - 1 ? 'Finish' : 'Next'}
            {step < totalSteps - 1 && <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}